import { useParams, Link } from 'react-router-dom';
import NewsNavbar from '@/components/feature/NewsNavbar';
import NewsFooter from '@/components/feature/NewsFooter';
import { useNewsByCategory, useCategories } from '@/hooks/useNews';
import { useSEO } from '@/hooks/useSEO';

function newsHref(item: { id: string; slug?: string }): string {
  return item.slug ? `/haber/${item.slug}` : `/haber/${item.id}`;
}

export default function NewsCategoryPage() {
  const { id } = useParams<{ id: string }>();
  const { news: categoryNews } = useNewsByCategory(id || '');
  const { categories } = useCategories();
  const category = id ? categories.find(c => c.id === id) : null;

  useSEO({
    title: category ? `${category.name} Haberleri - Güncel ${category.name} Teknoloji Haberleri` : 'Kategori',
    description: category
      ? `${category.name} kategorisindeki en güncel teknoloji haberleri. TeknoNovaHub'da ${categoryNews.length}+ ${category.name} haberi, analizler ve uzman yorumları.`
      : undefined,
    keywords: category ? `${category.name}, ${category.name} haberleri, teknoloji, TeknoNovaHub` : undefined,
    url: `/kategori/${id}`,
    type: 'website',
    schema: category ? {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Ana Sayfa', item: 'https://teknonovahub.com' },
        { '@type': 'ListItem', position: 2, name: `${category.name} Haberleri`, item: `https://teknonovahub.com/kategori/${id}` },
      ],
    } : undefined,
  });

  if (categories.length > 0 && !category) {
    return (
      <div className="min-h-screen bg-[#0A0E27] flex items-center justify-center">
        <div className="text-center">
          <i className="ri-folder-unknow-line text-6xl text-white/20 mb-4" />
          <h1 className="text-white font-bold text-2xl mb-2">Kategori Bulunamadı</h1>
          <Link to="/" className="text-[#00D9FF] hover:underline cursor-pointer">Ana Sayfaya Dön</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0E27] font-sans">
      <NewsNavbar />
      <main className="pt-28 md:pt-32">
        <section className="max-w-7xl mx-auto px-6 mb-8">
          <nav className="flex items-center gap-2 text-sm text-white/40 mb-4">
            <Link to="/" className="hover:text-white transition-colors cursor-pointer">Ana Sayfa</Link>
            <i className="ri-arrow-right-s-line" />
            <span className="text-white">{category?.name}</span>
          </nav>
          {category && (
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 flex items-center justify-center rounded-2xl" style={{ backgroundColor: `${category.color}20` }}>
                <i className={`${category.icon} text-2xl`} style={{ color: category.color }} />
              </div>
              <div>
                <h1 className="text-3xl md:text-4xl font-black text-white">{category.name}</h1>
                <p className="text-white/50 text-sm">{categoryNews.length} haber</p>
              </div>
            </div>
          )}
        </section>

        <section className="max-w-7xl mx-auto px-6 pb-16">
          {categoryNews.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {categoryNews.map((item) => (
                <Link key={item.id} to={newsHref(item)} className="group cursor-pointer">
                  <div className="relative overflow-hidden rounded-xl mb-4">
                    <div className="aspect-[16/10]">
                      <img src={item.image} alt={item.title} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500" />
                    </div>
                    <span
                      className="absolute top-3 left-3 px-2 py-1 text-[10px] font-bold rounded-full"
                      style={{ backgroundColor: `${category?.color}20`, color: category?.color }}
                    >
                      {category?.name}
                    </span>
                  </div>
                  <h3 className="text-white font-bold text-base mb-2 line-clamp-2 group-hover:text-[#00D9FF] transition-colors">{item.title}</h3>
                  <p className="text-white/50 text-sm line-clamp-2 mb-3">{item.excerpt}</p>
                  <div className="flex items-center gap-3 text-white/40 text-xs">
                    <span>{item.author}</span><span>·</span><span>{item.date}</span><span>·</span><span>{item.read_time} okuma</span>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-16">
              <i className="ri-news-line text-5xl text-white/20 mb-4 block" />
              <p className="text-white/50">Bu kategoride henüz haber bulunmuyor.</p>
            </div>
          )}
        </section>

        <section className="bg-white/5 border-y border-white/10 py-12">
          <div className="max-w-7xl mx-auto px-6">
            <h2 className="text-xl font-bold text-white mb-6">Diğer Kategoriler</h2>
            <div className="flex flex-wrap gap-3">
              {categories.filter(c => c.id !== id).map((cat) => (
                <Link
                  key={cat.id}
                  to={`/kategori/${cat.id}`}
                  className="flex items-center gap-2 px-4 py-2.5 bg-white/5 border border-white/10 rounded-full hover:bg-white/10 transition-all duration-300 cursor-pointer"
                >
                  <span className="w-8 h-8 flex items-center justify-center rounded-full" style={{ backgroundColor: `${cat.color}20` }}>
                    <i className={`${cat.icon} text-sm`} style={{ color: cat.color }} />
                  </span>
                  <span className="text-white text-sm font-medium">{cat.name}</span>
                </Link>
              ))}
            </div>
          </div>
        </section>
      </main>
      <NewsFooter />
    </div>
  );
}
